import React, { useCallback, useState } from 'react';

import { useKitTranslate } from 'common/components/kit-shared/useKitTranslate';
import { showToast } from 'utils/toasts';
import { ToastTypes } from 'common/types/toasts';
import { useKitWorkspace } from 'containers/KitWorkspaceStorage/context';
import { deleteAssembly } from 'containers/KitWorkspaceStorage/api';
import type { IAssembly } from 'containers/KitWorkspaceStorage/types';
import { assemblyDisplayName, buildSpecSummary, assemblyQty } from 'common/components/kit-shared/assemblySummary';
import AssemblyName from 'common/components/kit-shared/AssemblyName';
import AssemblyFormDialog from 'common/components/kit-shared/AssemblyFormDialog';
import ConfirmDialog from 'common/components/kit-shared/ConfirmDialog';

import Button from 'common/components/Button';
import { ButtonSchema, ButtonSize } from 'common/components/Button/constants';
import styles from './StandaloneAssemblyList.module.scss';

interface IStandaloneAssemblyListProps {
  assemblies: IAssembly[];
  // open the kit picker for a given assembly (Add to kit)
  onAddToKit?: (assembly: IAssembly) => void;
  emptyText?: string;
}

const AssemblyRow = ({
  assembly, onAddToKit, index,
}: { assembly: IAssembly; onAddToKit?: (a: IAssembly) => void; index: number }) => {
  const t = useKitTranslate();
  const { refetch } = useKitWorkspace();
  const [editing, setEditing] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  const spec = buildSpecSummary(assembly);
  const qty = assemblyQty(assembly);

  const confirmDelete = useCallback(async () => {
    setBusy(true);
    try {
      await deleteAssembly(assembly.uuid);
      showToast(ToastTypes.success, t('texts.kits.toast.assemblyDeleted'));
      // Row unmounts on refetch; no need to reset showDelete.
      await refetch();
    } catch {
      showToast(ToastTypes.error, t('texts.kits.toast.updateError'));
      setBusy(false);
    }
  }, [assembly.uuid, refetch, t]);

  return (
    <li className={styles.row}>
      <div className={styles.lead}>
        <span className={styles.num}>{index + 1}</span>
        <div className={styles.info}>
          <span className={styles.nameRow}>
            <AssemblyName assembly={assembly} nameClassName={styles.name} />
            {qty > 1 && <span className={styles.qtyBadge}>{`×${qty}`}</span>}
          </span>
          {spec && <span className={styles.spec}>{spec}</span>}
          {assembly.notes && <span className={styles.notes}>{assembly.notes}</span>}
        </div>
      </div>

      <div className={styles.actions}>
        {onAddToKit && (
          <Button
            schema={ButtonSchema.primary}
            size={ButtonSize.small}
            className={styles.addToKitBtn}
            onClick={() => onAddToKit(assembly)}
            disabled={busy}
          >
            {t('texts.kits.addToKit')}
          </Button>
        )}
        <button
          type="button"
          className={styles.iconBtn}
          onClick={() => setEditing(true)}
          disabled={busy}
          aria-label={t('texts.kits.edit')}
          title={t('texts.kits.edit')}
        >
          &#9998;
        </button>
        <button
          type="button"
          className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
          onClick={() => setShowDelete(true)}
          disabled={busy}
          aria-label={t('texts.kits.delete')}
          title={t('texts.kits.delete')}
        >
          <svg
            className={styles.iconSvg}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-2 14a2 2 0 01-2 2H9a2 2 0 01-2-2L5 6" />
          </svg>
        </button>
      </div>

      {editing && (
        <AssemblyFormDialog
          assembly={assembly}
          onCancel={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      )}

      {showDelete && (
        <ConfirmDialog
          title={t('texts.kits.deleteAssembly.title')}
          message={t('texts.kits.deleteAssembly.question', {
            name: assemblyDisplayName(assembly) || t('texts.kits.untitledName'),
          })}
          note={t('texts.kits.deleteAssembly.note')}
          confirmLabel={t('texts.kits.deleteAssembly.confirmBtn')}
          danger
          busy={busy}
          onConfirm={confirmDelete}
          onCancel={() => setShowDelete(false)}
        />
      )}
    </li>
  );
};

const StandaloneAssemblyList = ({ assemblies, onAddToKit, emptyText }: IStandaloneAssemblyListProps) => {
  const t = useKitTranslate();
  if (!assemblies.length) {
    return <p className={styles.empty}>{emptyText || t('texts.kits.assemblies.empty')}</p>;
  }
  return (
    <ul className={styles.list}>
      {assemblies.map((a, i) => (
        <AssemblyRow
          key={a.uuid}
          assembly={a}
          onAddToKit={onAddToKit}
          index={i}
        />
      ))}
    </ul>
  );
};

export default StandaloneAssemblyList;
